import { CalculatedValueDataEntity, OutputFormatEnum } from './calculated-value-data-entity';
import { Validators } from '@angular/forms';

/**
 * IssueDateDataEntity extends CalculatedValueDataEntity so designers can configure
 * conditional formula logic for the issue date.  When no formula condition matches
 * the default "otherwise" behaviour is today's date (handled server-side).
 *
 * Backward-compatibility notes
 * - `canUserEdit`  → getter/setter alias for `allowEdit`
 * - `initialValue` → getter/setter alias for `formattedValue` (server-populated display value)
 * - `getEffectiveValue()` returns `editedValue || formattedValue`
 */
export class IssueDateDataEntity extends CalculatedValueDataEntity {
  /** @deprecated Use `allowEdit`.  Kept for backward-compat with serialised workflow JSON. */
  get canUserEdit(): boolean {
    return this.allowEdit ?? false;
  }
  set canUserEdit(value: boolean) {
    this.allowEdit = value;
  }

  /**
   * Maps to `formattedValue` (the server-calculated display date).
   * The input component continues to reference `entity.initialValue` without changes.
   */
  get initialValue(): string {
    return this.formattedValue;
  }
  set initialValue(value: string) {
    this.formattedValue = value;
  }

  constructor(options?: Partial<IssueDateDataEntity>) {
    super(options);

    this.dataEntityTypeCode = 'issue-date-data-entity';
    this.dataEntityTypeDescription =
      'Use this entity type to capture the issue date for the application. ' +
      'Conditional formula criteria can be configured to calculate the date; ' +
      'when no condition matches, today\'s date is used by default.';
    this.dataEntityTypeName = 'Issue Date';
    this.hasEditor = true;
    this.allowEditFormula = true;
    this.allowEditVisibleToUser = true;

    // Default to Date output format
    if (this.outputFormat == null) {
      this.outputFormat = OutputFormatEnum.Date;
    }

    if (options) {
      Object.assign(this, options);

      // Sync canUserEdit <-> allowEdit so that either key in serialised JSON works.
      if (options.canUserEdit !== undefined && options.allowEdit === undefined) {
        this.allowEdit = options.canUserEdit;
      } else if (options.allowEdit !== undefined) {
        // canUserEdit setter will be called via Object.assign if present in options,
        // but ensure it is consistent when only allowEdit was provided.
      }
    }
  }

  /** Returns the user-edited value when present, otherwise the server-calculated date. */
  getEffectiveValue(): string {
    return this.editedValue || this.formattedValue;
  }

  override getValidators(): any[] {
    if (this.allowEdit) {
      return [Validators.required];
    }
    return [Validators.nullValidator];
  }
}
